import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import AuthModal from '../components/AuthModal';
import { useAuth } from '../contexts/AuthContext';

const { FiArrowRight, FiTrendingUp, FiTarget, FiAward, FiUsers, FiVideo, FiBookOpen, FiLogIn, FiCheckCircle } = FiIcons;

function LandingPage() {
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [authMode, setAuthMode] = useState('signup');
  const { user } = useAuth();

  const handleGetStarted = (mode = 'signup') => {
    if (user) {
      window.location.href = '#/dashboard';
    } else {
      setAuthMode(mode);
      setShowAuthModal(true);
    }
  };

  const features = [
    { icon: FiTarget, title: 'Professional Sports Resume', description: 'Create stunning public profiles that showcase your athletic achievements, stats, and highlights.' },
    { icon: FiTrendingUp, title: 'Scholarship Timeline', description: 'Track your progress with customizable milestones and tasks tailored to your sport.' },
    { icon: FiBookOpen, title: 'College Database', description: 'Access comprehensive information on college athletic programs and save your favorites.' },
    { icon: FiVideo, title: 'Content Planning', description: 'Get guidance on creating compelling highlight videos that showcase your abilities.' },
    { icon: FiUsers, title: 'Guardian Access', description: 'Parents and guardians can co-manage accounts and track progress together.' },
    { icon: FiAward, title: 'Export & Share', description: 'Generate PDF resumes and share professional profiles with coaches and recruiters.' },
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-primary to-blue-700 text-white py-12 sm:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <motion.h1
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="text-3xl sm:text-5xl md:text-7xl font-space-grotesk font-bold mb-4 sm:mb-6 leading-tight"
            >
              Your Blueprint to a{' '}
              <span className="text-accent block">College Scholarship</span>
            </motion.h1>
            <motion.p
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="text-base sm:text-xl md:text-2xl mb-6 sm:mb-8 max-w-3xl mx-auto font-inter px-2"
            >
              The ultimate recruiting dashboard. High school athletes get the tools to get noticed. Parents get the timeline to ensure nothing falls through the cracks.
            </motion.p>
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.4 }}
              className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center items-center px-4"
            >
              <button
                onClick={() => handleGetStarted('signup')}
                className="w-full sm:w-auto bg-accent text-secondary px-6 sm:px-8 py-3 sm:py-4 rounded-lg font-semibold text-base sm:text-lg hover:bg-yellow-300 transition-all btn-primary flex items-center justify-center space-x-2"
              >
                <span>Start Free 14-Day Trial</span>
                <SafeIcon icon={FiArrowRight} className="w-5 h-5" />
              </button>
              <button
                onClick={() => handleGetStarted('signin')}
                className="w-full sm:w-auto border-2 border-white text-white px-6 sm:px-8 py-3 sm:py-4 rounded-lg font-semibold text-base sm:text-lg hover:bg-white hover:text-primary transition-all flex items-center justify-center space-x-2"
              >
                <SafeIcon icon={FiLogIn} className="w-5 h-5" />
                <span>Sign In</span>
              </button>
              <Link to="/resume/demo" className="text-white underline hover:text-accent transition-colors text-base sm:text-lg">
                View Demo Resume
              </Link>
            </motion.div>
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.6, delay: 0.6 }}
              className="text-blue-100 mt-4 text-sm"
            >
              No credit card required • Full access for 14 days
            </motion.p>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-12 sm:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10 sm:mb-16">
            <h2 className="text-2xl sm:text-4xl font-space-grotesk font-bold text-secondary mb-3 sm:mb-4">
              Take the Chaos Out of Recruiting.
            </h2>
            <p className="text-base sm:text-xl text-gray-600 max-w-2xl mx-auto">
              Stop relying on messy spreadsheets. TheNextlete gives you a professional CRM, timeline tracker, and shareable athlete profile all in one place.
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
            {features.map((feature, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="bg-gray-50 p-6 sm:p-8 rounded-xl hover:shadow-lg transition-shadow"
              >
                <div className="w-12 h-12 bg-primary rounded-lg flex items-center justify-center mb-4 sm:mb-6">
                  <SafeIcon icon={feature.icon} className="w-6 h-6 text-white" />
                </div>
                <h3 className="text-lg sm:text-xl font-space-grotesk font-semibold text-secondary mb-3 sm:mb-4">
                  {feature.title}
                </h3>
                <p className="text-gray-600 leading-relaxed text-sm sm:text-base">{feature.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section className="py-12 sm:py-20 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10 sm:mb-16">
            <h2 className="text-2xl sm:text-4xl font-space-grotesk font-bold text-secondary mb-3 sm:mb-4">
              Simple, Transparent Pricing
            </h2>
            <p className="text-base sm:text-xl text-gray-600 mb-6 sm:mb-8">
              Start with a free 14-day trial. No credit card required.
            </p>
          </div>
          <div className="bg-gradient-to-br from-primary to-blue-700 text-white rounded-2xl p-6 sm:p-8 shadow-xl">
            <div className="text-center mb-6 sm:mb-8">
              <div className="inline-block bg-accent text-secondary px-4 py-1 rounded-full text-sm font-bold mb-4">
                14-DAY FREE TRIAL
              </div>
              <h3 className="text-2xl sm:text-3xl font-space-grotesk font-bold mb-2">
                Try Pro Features Free
              </h3>
              <p className="text-blue-100 text-sm sm:text-base">
                Full access to all features. Cancel anytime during trial.
              </p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6 mb-6 sm:mb-8">
              <div className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-lg p-5 sm:p-6">
                <div className="text-center mb-4">
                  <div className="text-3xl sm:text-4xl font-bold mb-2">$12</div>
                  <div className="text-blue-100">/month</div>
                </div>
                <ul className="space-y-2 sm:space-y-3">
                  {['Unlimited College Tracking', 'Full Timeline & Deadlines', 'Guardian Access', 'Public Resume', 'Video Highlights', 'Priority Support'].map((feature, index) => (
                    <li key={index} className="flex items-start space-x-2">
                      <SafeIcon icon={FiCheckCircle} className="w-4 h-4 sm:w-5 sm:h-5 text-green-300 flex-shrink-0 mt-0.5" />
                      <span className="text-sm">{feature}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <div className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-lg p-5 sm:p-6 relative">
                <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                  <span className="bg-green-500 text-white px-3 py-1 rounded-full text-xs font-bold">SAVE 30%</span>
                </div>
                <div className="text-center mb-4">
                  <div className="text-3xl sm:text-4xl font-bold mb-2">$99</div>
                  <div className="text-blue-100">/year</div>
                  <div className="text-sm text-green-300 mt-1">($8.25/month)</div>
                </div>
                <ul className="space-y-2 sm:space-y-3">
                  {['Everything in Monthly', 'Save $45 per year', 'Lock in current pricing', 'Priority feature requests', 'Exclusive content updates', 'Annual planning session'].map((feature, index) => (
                    <li key={index} className="flex items-start space-x-2">
                      <SafeIcon icon={FiCheckCircle} className="w-4 h-4 sm:w-5 sm:h-5 text-green-300 flex-shrink-0 mt-0.5" />
                      <span className="text-sm">{feature}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
            <div className="text-center">
              <button
                onClick={() => handleGetStarted('signup')}
                className="w-full sm:w-auto bg-white text-primary px-6 sm:px-8 py-3 sm:py-4 rounded-lg font-semibold text-base sm:text-lg hover:bg-gray-100 transition-colors inline-flex items-center justify-center space-x-2"
              >
                <span>Start Your Free Trial</span>
                <SafeIcon icon={FiArrowRight} className="w-5 h-5" />
              </button>
              <p className="text-blue-100 text-sm mt-4">No credit card required • Cancel anytime</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-12 sm:py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <h2 className="text-2xl sm:text-4xl font-space-grotesk font-bold text-secondary mb-4 sm:mb-6">
              Ready to Take Control of Your Future?
            </h2>
            <p className="text-base sm:text-xl text-gray-600 mb-6 sm:mb-8">
              Join thousands of student-athletes who are already using TheNextlete to secure their college scholarships.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center items-center">
              <button
                onClick={() => handleGetStarted('signup')}
                className="w-full sm:w-auto bg-primary text-white px-6 sm:px-8 py-3 sm:py-4 rounded-lg font-semibold text-base sm:text-lg hover:bg-blue-700 transition-all btn-primary inline-flex items-center justify-center space-x-2"
              >
                <span>Start Free Trial</span>
                <SafeIcon icon={FiArrowRight} className="w-5 h-5" />
              </button>
              <button
                onClick={() => handleGetStarted('signin')}
                className="w-full sm:w-auto border-2 border-primary text-primary px-6 sm:px-8 py-3 sm:py-4 rounded-lg font-semibold text-base sm:text-lg hover:bg-primary hover:text-white transition-all inline-flex items-center justify-center space-x-2"
              >
                <SafeIcon icon={FiLogIn} className="w-5 h-5" />
                <span>Sign In</span>
              </button>
            </div>
          </motion.div>
        </div>
      </section>

      <AuthModal isOpen={showAuthModal} onClose={() => setShowAuthModal(false)} defaultMode={authMode} />
    </div>
  );
}

export default LandingPage;